import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistOfficialInformationQuery,
  ArtistOfficialInformationQueryVariables,
  FetchNewArtistOfficialInformationQuery,
  FetchNewArtistOfficialInformationQueryVariables,
  NotificationBoxFragmentDoc,
  OrganizationNotificationConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistOfficialInformationQuery,
    FetchNewArtistOfficialInformationQueryVariables
  >({
    query: FETCH_NEW_QUERY,
    variables: { pageInput: { limit: 20 }, artistId, id: artistId },
    fields: [
      {
        fieldName: 'notifications',
        fragment: NotificationBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, networkStatus, fetchMore } = useQuery<
    ArtistOfficialInformationQuery,
    ArtistOfficialInformationQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { pageInput: { limit: 5 }, artistId, id: artistId },
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      refetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage:
        !!data?.organizationOfficialArtistNotifications?.pageInfo.hasNextPage,
      notifications: data?.organizationOfficialArtistNotifications ?? null,
      artist: data?.artist ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              limit: 5,
              cursor:
                data?.organizationOfficialArtistNotifications?.pageInfo
                  .endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistOfficialInformation(
    $id: ID!
    $artistId: String!
    $pageInput: PageInput
  ) {
    organizationOfficialArtistNotifications(
      artistId: $artistId
      pageInput: $pageInput
    ) {
      ...OrganizationNotificationConnection
    }
    artist(id: $id) {
      displayName
      avatar {
        uri
        smallUri
      }
    }
  }
  ${OrganizationNotificationConnectionFragmentDoc}
`

const FETCH_NEW_QUERY = gql`
  query FetchNewArtistOfficialInformation(
    $id: ID!
    $artistId: String!
    $pageInput: PageInput
  ) {
    organizationOfficialArtistNotifications(
      artistId: $artistId
      pageInput: $pageInput
    ) {
      ...OrganizationNotificationConnection
    }
    artist(id: $id) {
      displayName
      avatar {
        uri
        smallUri
      }
    }
  }
  ${OrganizationNotificationConnectionFragmentDoc}
`
